// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@tps/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface linkGroups {
    title: string;
    links: (
        | {
              title: string;
              url: string;
              type?: string | undefined;
              id: string;
              parentId?: string | undefined;
              excludeFromNavigation?: boolean | undefined;
              children?: PageLinkModel[] | undefined;
              openInNewWindow?: boolean;
          }
        | undefined
    )[];
}

interface MiniSignInProps {
    linkGroups?: linkGroups[];
    onSignOutHandler: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentBillTo: getCurrentBillToState(state),
        displayChangeCustomerLink: getSession(state).displayChangeCustomerLink,
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        isPunchOutSession: getIsPunchOutSession(state),
    };
};

type Props = MiniSignInProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    mainTitle?: TypographyPresentationProps;
    linksSectionWrapper?: InjectableCss;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        link?: LinkPresentationProps;
    };
    subTitle?: TypographyPresentationProps;
    actionsWrapper?: InjectableCss;
    changeCustomerLink?: LinkPresentationProps;
    signOutLink?: LinkPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {};

const mapDispatchToProps = {
    loadCurrentBillTo,
};

const MiniMyAccount: FC<Props> = ({ linkGroups, onSignOutHandler, extendedStyles, loadCurrentBillTo, isPunchOutSession, changeCustomerUrl, currentBillTo, displayChangeCustomerLink }) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));

    useEffect(() => {
        if (!currentBillTo.value && !currentBillTo.isLoading) {
            setTimeout(() => {
                loadCurrentBillTo();
            }, 500);
        }
    }, []);

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.subTitle}>{linkGroup.title}</Typography>}
                    {linkGroup.links.map(
                        link =>
                            link?.url && (
                                <StyledWrapper key={`${link?.title}_${link?.url}`} {...styles.linkItems?.linkWrapper}>
                                    <Link
                                        onClick={() => {
                                            hideAll();
                                        }}
                                        href={link?.url}
                                        {...styles.linkItems?.link}
                                        target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                        aria-label={link?.title}
                                    >
                                        {link?.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            {currentBillTo && (
                <Typography
                    {...styles.mainTitle}
                >{`${currentBillTo.value?.companyName} #${currentBillTo.value?.customerNumber}`}</Typography>
            )}
            <StyledWrapper {...styles.linksSectionWrapper}>{linksSection}</StyledWrapper>
            <StyledWrapper {...styles.actionsWrapper}>
                {displayChangeCustomerLink && (
                    <Link
                        {...styles.changeCustomerLink}
                        aria-label={translate("Change Ship-To")}
                        href={changeCustomerUrl}
                        onClick={() => {
                            hideAll();
                        }}
                    >
                        {translate("Change Ship-To")}
                    </Link>
                )}
                <Link
                    {...styles.signOutLink}
                    onClick={() => {
                        onSignOutHandler();
                        hideAll();
                    }}
                    aria-label={translate("Sign Out")}
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniMyAccount);

